import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';

const ConditionDisplay = ({ conditions = null }) => {
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipText, setTooltipText] = useState('');

  // Handle both old string format and new JSON object format
  let conditionList = [];
  let iconList = [];

  if (!conditions) {
    return null;
  }

  // Check if conditions is a string (old format) or object (new format)
  if (typeof conditions === 'string') {
    if (conditions.trim() === '') {
      return null;
    }
    conditionList = conditions.split(',').map(c => c.trim()).filter(c => c !== '');
    // Use default icons for old format
    iconList = conditionList.map(() => 'question');
  } else if (typeof conditions === 'object' && conditions !== null) {
    // New JSON object format
    const title = conditions.title || '';
    const icon = conditions.icon || '';
    
    // Handle empty strings
    if (title.trim() === '' && icon.trim() === '') {
      return null;
    }
    
    conditionList = title.split(',').map(c => c.trim()).filter(c => c !== '');
    
    // Split icons but don't filter out empty ones initially - we'll handle them
    const rawIconList = icon.split(',').map(c => c.trim());
    
    // Convert PascalCase to kebab-case for phosphor icons and handle empty values
    iconList = rawIconList.map(iconName => {
      // Handle empty or whitespace-only icons
      if (!iconName || iconName.trim() === '') {
        return 'question';
      }
      
      // Convert PascalCase to kebab-case
      const convertedIcon = iconName.trim().replace(/([A-Z])/g, '-$1').toLowerCase().replace(/^-/, '');
      
      // Return question mark if conversion resulted in empty string
      return convertedIcon || 'question';
    });
    
    // Ensure both arrays have the same length
    if (conditionList.length !== iconList.length) {
      // Pad shorter array with defaults
      while (iconList.length < conditionList.length) {
        iconList.push('question');
      }
      while (conditionList.length < iconList.length) {
        conditionList.push('Unknown');
      }
    }
    
    // If no valid conditions, return null
    if (conditionList.length === 0) {
      return null;
    }
  } else {
    return null;
  }

  if (conditionList.length === 0) {
    return null;
  }

  const handleMouseEnter = (text, event) => {
    // Position near cursor to avoid stacking/transform issues
    setTooltipPosition({ x: event.clientX + 10, y: event.clientY + 8 });
    setTooltipText(text);
  };

  const handleMouseMove = (event) => {
    setTooltipPosition({ x: event.clientX + 10, y: event.clientY + 8 });
  };

  const handleMouseLeave = () => {
    setTooltipText('');
  };

  // Limit to 2 icons; show ellipsis if more so it stays on the same row
  const MAX_ICONS = 2;
  const displayedTitles = conditionList.slice(0, MAX_ICONS);
  const displayedIcons = iconList.slice(0, MAX_ICONS);
  const remainingCount = Math.max(0, conditionList.length - MAX_ICONS);
  const remainingTitles = remainingCount > 0 ? conditionList.slice(MAX_ICONS) : [];

  return (
    <div className="condition-box">
      <div
        className="condition-filter-icon"
        onMouseEnter={(e) => handleMouseEnter('Filters', e)}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        aria-label="Filters"
      >
        <Icon name="funnel" size={12} weight="regular" />
      </div>
      <div className="condition-display">
        {displayedTitles.map((title, index) => {
          const iconName = displayedIcons[index];
          const conditionTitle = title || '';
          return (
            <div
              key={index}
              className="condition-icon-wrapper"
              onMouseEnter={(e) => handleMouseEnter(`Condition Applied: ${conditionTitle}`, e)}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              aria-label={conditionTitle}
            >
              <Icon 
                name={iconName} 
                size={14} 
                weight="regular"
                className="condition-icon"
              />
            </div>
          );
        })}
        {remainingCount > 0 && (
          <div
            className="condition-ellipsis"
            onMouseEnter={(e) => handleMouseEnter(`+${remainingCount} more:\n` + remainingTitles.join('\n'), e)}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            aria-label={`+${remainingCount} more`}
          >
            …
          </div>
        )}
      </div>
      {tooltipText && createPortal(
        (
          <div 
            className="condition-tooltip"
            style={{
              position: 'fixed',
              left: tooltipPosition.x,
              top: tooltipPosition.y,
              zIndex: 1000000
            }}
          >
            <div className="condition-tooltip-title">{tooltipText}</div>
          </div>
        ),
        document.body
      )}
    </div>
  );
};

export default ConditionDisplay;